import { useDispatch, useSelector } from "react-redux";
import { useEffect, useCallback, useState } from "react";
import { RootState } from "./redux/store";
import { useRequests } from "./requests";
import { setUser } from "./redux/reducers/authReducer";
import { authStorage, AuthTokens } from "./auth/authStorage";
import { AuthUser, isAuthUser, toAuthUser } from "../types/user";
import { 
  setupAuthInterceptors, 
  setupTokenRefreshScheduler,
  setupVisibilityListener,
  refreshTokenIfNeeded 
} from "./auth/authInterceptor";

// Manter compatibilidade com código existente
export const handleGetAccessToken = () => authStorage.getAccessToken();

export const useAuth = () => {
    const auth = useSelector((state: RootState) => state.auth);
    const dispatch = useDispatch();
    
    // Estado para controlar inicialização
    const [isInitialized, setIsInitialized] = useState(false);
    const [isInitializing, setIsInitializing] = useState(false);
    
    const { signIn, getUser } = useRequests();

    // Configurar interceptadores na primeira inicialização
    useEffect(() => {
        setupAuthInterceptors();
        
        const cleanupRefreshScheduler = setupTokenRefreshScheduler();
        const cleanupVisibilityListener = setupVisibilityListener();
        
        // Listener para eventos de expiração de token
        const handleTokenExpired = () => {
            console.log('🔄 Token expirado - fazendo logout automático');
            handleSignOut();
        };
        
        window.addEventListener('auth:token-expired', handleTokenExpired);
        
        return () => {
            cleanupRefreshScheduler();
            cleanupVisibilityListener();
            window.removeEventListener('auth:token-expired', handleTokenExpired);
        };
    }, []);

    const user = {
        user: auth.user,
    }

    /**
     * Inicializar usuário a partir do cache
     */
    const handleInitUser = useCallback(async () => {
        if (isInitializing) return;
        
        setIsInitializing(true);
        
        try {
            // Verificar se há dados em cache
            const cachedData = authStorage.getAuthData();
            
            if (cachedData && authStorage.isSessionActive()) {
                // Se há dados em cache válidos, usar diretamente
                dispatch(setUser(cachedData.user));
                console.log('✅ Usuário carregado do cache');
                
                // Verificar se precisa renovar token proativamente
                await refreshTokenIfNeeded();
                
            } else if (authStorage.getAccessToken()) {
                // Fallback: tentar buscar dados do servidor
                console.log('🔄 Buscando dados do usuário do servidor...');
                const response = await getUser();

                if (!response.detail && response.data?.user) {
                    dispatch(setUser(response.data.user));
                    
                    // Atualizar cache com dados do servidor
                    const tokens: AuthTokens = {
                        access: authStorage.getAccessToken(),
                        refresh: authStorage.getRefreshToken()
                    };
                    
                    // Converter para AuthUser se necessário
                    const authUser = isAuthUser(response.data.user) 
                        ? response.data.user 
                        : toAuthUser(response.data.user, (response.data.user as any).id || 0);
                    authStorage.saveAuthData(tokens, authUser);
                    console.log('✅ Usuário carregado do servidor e cache atualizado');
                } else {
                    // Token inválido, limpar dados
                    authStorage.clearAuthData();
                    console.log('⚠️ Token inválido - dados limpos');
                }
            }
        } catch (error) {
            console.error('❌ Erro ao inicializar usuário:', error);
            // Em caso de erro, limpar dados potencialmente corrompidos
            authStorage.clearAuthData();
        } finally {
            setIsInitialized(true);
            setIsInitializing(false);
        }
    }, [dispatch, getUser, isInitializing]);

    /**
     * Inicialização automática quando o hook é usado
     */
    useEffect(() => {
        if (!isInitialized && !isInitializing) {
            handleInitUser();
        }
    }, [handleInitUser, isInitialized, isInitializing]);

    const handlePermissionExists = (permissionCodename: string) => {
        // Verifica se o usuário está autenticado
        if (!auth?.user) {
            return false; // Não autenticado, sem permissão
        }
        
        // Por enquanto, usuários logados têm todas as permissões
        // TODO: Implementar sistema de permissões granular
        return true;
    }

    /**
     * Login melhorado com cache automático
     */
    const handleSignIn = async (email: string, password: string) => {
        try {
            const response = await signIn({ email, password });

            if (!response.detail && response.data) {
                const { user, access, refresh } = response.data;
                
                // Atualizar estado do Redux
                dispatch(setUser(user));

                // Salvar dados no cache seguro
                const tokens: AuthTokens = { access, refresh };
                const authUser = isAuthUser(user) ? user : toAuthUser(user, (user as any).id || 0);
                authStorage.saveAuthData(tokens, authUser);
                
                console.log('✅ Login realizado com sucesso');
            }

            return response;
        } catch (error) {
            console.error('❌ Erro no login:', error);
            throw error;
        }
    }

    /**
     * Logout melhorado com limpeza completa
     */
    const handleSignOut = useCallback(() => {
        // Limpar estado do Redux
        dispatch(setUser(null));

        // Limpar todos os dados de autenticação
        authStorage.clearAuthData();
        
        // Disparar evento customizado para outros componentes reagirem
        window.dispatchEvent(new CustomEvent('auth:logout'));
        
        console.log('👋 Logout realizado com sucesso');
    }, [dispatch]);

    /**
     * Verificar se usuário está logado (incluindo cache)
     */
    const isLoggedIn = useCallback(() => {
        return auth.user != null && authStorage.isLoggedIn();
    }, [auth.user]);

    /**
     * Forçar atualização do cache
     */
    const refreshUserData = useCallback(async () => {
        if (!authStorage.isLoggedIn()) return;
        
        try {
            const response = await getUser();
            if (!response.detail && response.data?.user) {
                dispatch(setUser(response.data.user));
                
                // Atualizar cache mantendo tokens existentes
                const tokens: AuthTokens = {
                    access: authStorage.getAccessToken(),
                    refresh: authStorage.getRefreshToken()
                };
                
                const authUser = isAuthUser(response.data.user) 
                    ? response.data.user 
                    : toAuthUser(response.data.user, (response.data.user as any).id || 0);
                authStorage.saveAuthData(tokens, authUser);
                console.log('🔄 Dados do usuário atualizados');
            }
        } catch (error) {
            console.error('❌ Erro ao atualizar dados do usuário:', error);
        }
    }, [dispatch, getUser]);

    /**
     * Obter estatísticas do cache (para debugging)
     */
    const getCacheStats = useCallback(() => {
        return authStorage.getCacheStats();
    }, []);

    return {
        user,
        isLogged: isLoggedIn(),
        isInitialized,
        isInitializing,
        handleInitUser,
        handlePermissionExists,
        handleSignIn,
        handleSignOut,
        refreshUserData,
        getCacheStats,
        
        // Métodos de compatibilidade
        isLoggedIn: isLoggedIn()
    };
}